import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Rocket, Lock } from 'lucide-react';
import { Config } from '../../types';

interface PreLaunchModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: Config;
  onAdminBypass: () => void;
}

export const PreLaunchModal: React.FC<PreLaunchModalProps> = ({ isOpen, onClose, config, onAdminBypass }) => {
  const [clickCount, setClickCount] = useState(0);

  useEffect(() => {
    if (!isOpen) setClickCount(0);
  }, [isOpen]);

  const handleAdminBypass = () => {
    setClickCount(prev => prev + 1);
    if (clickCount >= 4) {
      setClickCount(0);
      onAdminBypass();
    }
  };

  const primaryColor = config.primaryColor || '#eab308';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[500] bg-black/40 backdrop-blur-md"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[510] w-full max-w-md p-4"
          >
            <div className="bg-[#0f1118] border border-white/10 rounded-3xl p-8 flex flex-col items-center text-center shadow-2xl relative overflow-hidden">
              {/* Glow background */}
              <div 
                className="absolute -top-32 -left-32 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none"
                style={{ background: primaryColor }}
              />
              <div 
                className="absolute -bottom-32 -right-32 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none"
                style={{ background: primaryColor }}
              />

              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>

              <div 
                className="relative flex items-center justify-center mb-6 cursor-pointer"
                onClick={handleAdminBypass}
              >
                {config.logoUrl ? (
                  <img src={config.logoUrl} alt="Logo" className="w-24 h-24 md:w-32 md:h-32 object-contain animate-pulse drop-shadow-2xl" />
                ) : (
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-white/10 animate-pulse drop-shadow-2xl" />
                )}
                {clickCount > 0 && clickCount < 5 && (
                  <span className="absolute -top-1 -right-1 text-[10px] font-bold bg-red-500 text-white px-2 py-0.5 rounded-full z-10">
                    {5 - clickCount}
                  </span>
                )}
              </div>

              <h2 className="text-3xl font-black uppercase italic tracking-tighter text-white mb-2">
                Ya falta <span style={{ color: primaryColor }}>muy poco</span>
              </h2>
              
              <p className="text-white/70 text-sm md:text-base leading-relaxed mb-8">
                ¡Habilitaremos los registros en solo unos días! Quédate atento a nuestras redes para ser el primero en unirte a {config.pageName || 'la plataforma'}.
              </p>

              <button
                onClick={onClose}
                className="w-full py-4 rounded-2xl font-black uppercase tracking-widest text-sm text-black transition-transform hover:scale-[1.02] active:scale-95 shadow-lg relative overflow-hidden group"
                style={{ background: primaryColor }}
              >
                <span className="relative z-10">Entendido</span>
                <div 
                  className="absolute inset-0 w-[200%] bg-gradient-to-r from-transparent via-white/60 to-transparent"
                  style={{ animation: 'shimmer 3.5s infinite linear' }}
                />
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
